import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageUp } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAdminApi } from "@/hooks/useAdminApi";
import { fileUrl, errMsg, NETWORK_INFO } from "@/lib/api";

const WalletCard = ({ w, onReload }) => {
  const call = useAdminApi();
  const [address, setAddress] = useState(w.address);
  const [enabled, setEnabled] = useState(w.enabled);
  const n = w.network.toLowerCase();
  const save = async () => {
    try { await call("put", `/admin/wallets/${w.network}`, { address, enabled }); toast.success(`${w.network} wallet saved`); onReload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await call("post", `/admin/wallets/${w.network}/qr`, fd); toast.success("QR code uploaded"); onReload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid={`wallet-card-${n}`} className="rounded-lg border border-white/10 bg-[#0F1117] p-5 grid sm:grid-cols-[120px_1fr] gap-5">
      <div className="bg-white rounded-md w-[120px] h-[120px] p-2 grid place-items-center text-gray-400 text-xs text-center">
        {w.qr_path ? <img src={fileUrl(w.qr_path)} alt="QR" className="w-full h-full object-contain" /> : w.address ? <QRCodeSVG value={w.address} size={104} /> : "No QR yet"}
      </div>
      <div className="space-y-3 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-mono font-bold" style={{ color: NETWORK_INFO[w.network].color }}>USDT · {w.network} <span className="text-gray-500 font-normal text-xs">{NETWORK_INFO[w.network].chain}</span></p>
          <label className="flex items-center gap-2 text-xs text-gray-400"><input data-testid={`wallet-enabled-${n}`} type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled</label>
        </div>
        <input data-testid={`wallet-address-input-${n}`} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Paste wallet address"
          className="w-full h-10 px-3 rounded-md bg-[#08090C] border border-white/10 font-mono text-xs focus:outline-none focus:border-emerald-500/60" />
        <div className="flex gap-2">
          <button data-testid={`wallet-save-${n}`} onClick={save} className="h-9 px-4 rounded-md bg-emerald-500 text-[#08090C] font-bold text-sm hover:bg-emerald-400">Save</button>
          <label className="h-9 px-4 rounded-md border border-white/10 text-sm inline-flex items-center gap-2 cursor-pointer hover:border-emerald-500/60">
            <ImageUp size={14} /> Upload QR
            <input data-testid={`wallet-qr-upload-${n}`} type="file" accept="image/*" hidden onChange={upload} />
          </label>
          {w.qr_path && (
            <button data-testid={`wallet-qr-remove-${n}`} onClick={async () => { await call("delete", `/admin/wallets/${w.network}/qr`); toast.success("QR image removed"); onReload(); }}
              className="h-9 px-3 rounded-md text-sm text-gray-400 hover:text-red-400">Remove QR</button>
          )}
        </div>
      </div>
    </div>
  );
};

export const WalletsTab = () => {
  const call = useAdminApi();
  const [wallets, setWallets] = useState([]);
  const load = () => call("get", "/admin/wallets").then(setWallets);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">Networks only appear at checkout once they have an address and are enabled. If you don't upload a QR image, one is generated from the address.</p>
      <div className="grid lg:grid-cols-2 gap-4">{wallets.map((w) => <WalletCard key={`${w.network}-${w.address}-${w.qr_path}`} w={w} onReload={load} />)}</div>
    </div>
  );
};
